import { ref, computed } from "vue";
import { defineStore } from "pinia";
import {
  checkRow,
  addLetter,
  removeLetter,
  resetWordle,
} from "@/logic/wordle6Logic";
import { serverTimestamp } from "firebase/firestore";
import {
  fetchGameState,
  fetchNewestGame,
  saveGameState,
} from "@/helpers/firestore/Wordle6GameService";
import { setLocalStorageItem } from "@/helpers/utils/local-storage";
import { useAuthStore } from "@/stores/modules/auth/auth";
import type { WordleGameDocument } from "@kekkle/shared/types/documents/wordle-game-document/wordle-game-document";
import type { WordleGameState } from "@kekkle/shared/types/fields/wordle-game-state/wordle-game-state";
import { calculateWordleScore } from "@kekkle/shared/helpers/worlde/calculate-wordle-score";
import { reconstructGameState } from "@kekkle/shared/helpers/worlde/reconstruct-game-state";
import { handleFirestoreException } from "@/helpers/exceptions/handler";
import type { FirestoreError } from "firebase/firestore";

type ExtendedCharacterState = {
  character: string;
  isCorrect: boolean;
  isLocked: boolean;
  isPresent?: boolean;
};

type ExtendedWordleRow = ExtendedCharacterState[];
type ExtendedWordleGameState = ExtendedWordleRow[];

export const useWordle6Store = defineStore("wordle6", () => {
  const authStore = useAuthStore();

  const gameDoc = ref<WordleGameDocument | null>(null);
  const gameState = ref<ExtendedWordleGameState>(resetWordle());
  const possibleWords = ref<string[]>(
    JSON.parse(localStorage.getItem("wordle6PossibleWords") ?? "[]"),
  );
  const invalidWord = ref<boolean>(false);
  const fetchError = ref<string | null>(null);

  const correctWord = computed<string | null>(
    () => gameDoc.value?.word ?? null,
  );
  const matchId = computed<string | null>(() => gameDoc.value?._id ?? null);
  const matchDate = computed(() => gameDoc.value?.date);

  const isTyping = computed<boolean>(() => {
    return gameState.value.some((row: ExtendedWordleRow) =>
      row.some(
        (cell: ExtendedCharacterState) =>
          !cell.isLocked && cell.character !== "",
      ),
    );
  });

  const currentRow = computed<number>(() => {
    for (let i: number = 0; i < gameState.value.length; i++) {
      const row: ExtendedWordleRow | undefined = gameState.value[i];
      if (row && row.some((cell: ExtendedCharacterState) => !cell.isLocked)) {
        return i;
      }
    }
    return -1;
  });

  const currentColumn = computed<number>(() => {
    const row: number = currentRow.value;
    if (row === -1) return -1;
    const rowCells: ExtendedWordleRow | undefined = gameState.value[row];
    if (!rowCells) return -1;
    for (let i: number = 0; i < rowCells.length; i++) {
      const cell: ExtendedCharacterState | undefined = rowCells[i];
      if (cell && cell.character === "" && !cell.isLocked) {
        return i;
      }
    }
    const lastCell: ExtendedCharacterState | undefined =
      rowCells[rowCells.length - 1];
    if (lastCell && !lastCell.isLocked) {
      return rowCells.length - 1;
    }
    return -1;
  });

  const rowIsFilled = computed<boolean>(() => {
    if (currentRow.value === -1) return false;
    const row: ExtendedWordleRow | undefined =
      gameState.value[currentRow.value];
    return row
      ? row.every((cell: ExtendedCharacterState) => cell.character !== "")
      : false;
  });

  const hasWon = computed<boolean>(() => {
    return gameState.value.some((row: ExtendedWordleRow) =>
      row.every(
        (cell: ExtendedCharacterState) => cell.isCorrect && cell.isLocked,
      ),
    );
  });

  const hasFilled = computed<boolean>(() => {
    return gameState.value.every((row: ExtendedWordleRow) =>
      row.every((cell: ExtendedCharacterState) => cell.isLocked),
    );
  });

  const hasLost = computed<boolean>(() => hasFilled.value && !hasWon.value);
  const hasFinished = computed<boolean>(() => hasWon.value || hasLost.value);

  const numberOfTriesNeeded = computed<number>(() => {
    return gameState.value.filter((row: ExtendedWordleRow) =>
      row.some((cell: ExtendedCharacterState) => cell.isLocked),
    ).length;
  });

  const calculatedScore = computed<number>(() => {
    if (!hasFinished.value) return 0;
    return calculateWordleScore(gameState.value);
  });

  const guesses = computed<string[]>(() =>
    gameState.value
      .filter((row) => row.every((cell) => cell.isLocked))
      .map((row) => row.map((cell) => cell.character).join("")),
  );

  const correctCharacters = computed<string[]>(() => {
    const chars: string[] = [];
    gameState.value.forEach((row: ExtendedWordleRow) => {
      row.forEach((cell: ExtendedCharacterState) => {
        if (
          cell.isCorrect &&
          cell.isLocked &&
          !chars.includes(cell.character)
        ) {
          chars.push(cell.character);
        }
      });
    });
    return chars;
  });

  const presentCharacters = computed<string[]>(() => {
    const chars: string[] = [];
    gameState.value.forEach((row: ExtendedWordleRow) => {
      row.forEach((cell: ExtendedCharacterState) => {
        if (
          cell.isPresent &&
          cell.isLocked &&
          !chars.includes(cell.character)
        ) {
          chars.push(cell.character);
        }
      });
    });
    return chars;
  });

  const wrongCharacters = computed<string[]>(() => {
    const chars: string[] = [];
    gameState.value.forEach((row: ExtendedWordleRow) => {
      row.forEach((cell: ExtendedCharacterState) => {
        if (
          !cell.isCorrect &&
          !cell.isPresent &&
          cell.isLocked &&
          !chars.includes(cell.character)
        ) {
          chars.push(cell.character);
        }
      });
    });
    return chars;
  });

  function reset(): void {
    gameDoc.value = null;
    gameState.value = resetWordle();
    invalidWord.value = false;
    possibleWords.value = [];
    localStorage.removeItem("wordle6PossibleWords");
  }

  function setGameDoc(doc: WordleGameDocument): void {
    gameDoc.value = doc;
    gameState.value = JSON.parse(JSON.stringify(resetWordle()));
  }

  function setGameState(state: WordleGameState): void {
    gameState.value = state;
  }

  function setPossibleWords(words: string[]): void {
    possibleWords.value = words;
  }

  function setInvalidWord(invalid: boolean): void {
    invalidWord.value = invalid;
    if (invalid) {
      setTimeout(() => {
        invalidWord.value = false;
      }, 500);
    }
  }

  async function saveGameStateAction(): Promise<void> {
    if (!matchId.value || !authStore.authUuid) return;
    await saveGameState(matchId.value, authStore.authUuid, {
      guesses: guesses.value,
      user_id: authStore.authUuid,
      updated_at: serverTimestamp() as unknown as Date,
      has_finished: hasFinished.value,
      has_won: hasWon.value,
      has_lost: hasLost.value,
      number_of_tries_needed: numberOfTriesNeeded.value,
    });
  }

  async function fetchGameStateAction(params?: {
    matchId?: string;
    force?: boolean;
  }): Promise<void> {
    if (isTyping.value && !params?.force) return;

    fetchError.value = null;

    try {
      let gameId: string | undefined = params?.matchId;
      if (!gameId) {
        const newestGameDoc = await fetchNewestGame();
        if (newestGameDoc) {
          setGameDoc(newestGameDoc);
          gameId = newestGameDoc._id;
        }
      }
      if (!authStore.authUuid || !gameId) return;
      const fetchedGuesses = await fetchGameState(gameId, authStore.authUuid);
      if (isTyping.value && !params?.force) return;
      if (fetchedGuesses && correctWord.value) {
        setGameState(
          reconstructGameState(fetchedGuesses, correctWord.value, 6),
        );
      }
    } catch (e) {
      fetchError.value = handleFirestoreException({
        e: e as FirestoreError,
      });
    }
  }

  async function loadPossibleWords(): Promise<void> {
    if (possibleWords.value.length > 0) return;
    const wordListModule = await import(
      "@/assets/data/worlde/six-letter-words-NL.txt?raw"
    );
    const wordList = wordListModule.default.split("\n");
    setPossibleWords(wordList);
    setLocalStorageItem("wordle6PossibleWords", JSON.stringify(wordList));
  }

  async function initWordle(): Promise<void> {
    await fetchGameStateAction({ force: true });
    await loadPossibleWords();
  }

  async function keyPressed(key: string): Promise<void> {
    if (key === "backspace") {
      const newState = removeLetter(
        gameState.value,
        currentRow.value,
        currentColumn.value,
      );
      setGameState(newState);
    } else if (key === "enter") {
      const row: ExtendedWordleRow | undefined =
        gameState.value[currentRow.value];
      if (!row) return;
      const word: string = row.map((cell) => cell.character).join("");
      if (possibleWords.value.includes(word) || word === correctWord.value) {
        const newState = checkRow(
          gameState.value,
          correctWord.value!,
          currentRow.value,
        );
        setGameState(newState);
        await saveGameStateAction();
      } else {
        setInvalidWord(true);
      }
    } else {
      const newState = addLetter(
        gameState.value,
        currentRow.value,
        currentColumn.value,
        key,
      );
      setGameState(newState);
    }
  }

  return {
    gameDoc,
    gameState,
    possibleWords,
    invalidWord,
    fetchError,
    correctWord,
    matchId,
    matchDate,
    isTyping,
    currentRow,
    currentColumn,
    rowIsFilled,
    hasWon,
    hasFilled,
    hasLost,
    hasFinished,
    numberOfTriesNeeded,
    calculatedScore,
    correctCharacters,
    presentCharacters,
    wrongCharacters,
    reset,
    setGameDoc,
    setGameState,
    setPossibleWords,
    setInvalidWord,
    saveGameState: saveGameStateAction,
    fetchGameState: fetchGameStateAction,
    loadPossibleWords,
    initWordle,
    keyPressed,
  };
});
